import { MessageCircle } from "lucide-react";
import { Seo } from "../components/Seo";
import { PA_BASE, IMAGES, img, waLink } from "../config";
import { KineticHero } from "../components/KineticHero";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal, Overline } from "../components/motion";

const STEPS = [
  {
    n: "01",
    title: "Tell us the journey",
    body: "Route, dates, passengers, preferences. Two minutes in the form below, or one WhatsApp message.",
  },
  {
    n: "02",
    title: "We source the aircraft",
    body: "We canvass independent operators in our global network for suitable, available aircraft on your dates.",
  },
  {
    n: "03",
    title: "You receive options",
    body: "A short, considered set of options with transparent quotes — aircraft, operator standards and terms.",
  },
  {
    n: "04",
    title: "We arrange everything",
    body: "Slots, handling, catering, ground transport, overflight permissions — confirmed and managed to wheels-up.",
  },
];

const SCOPE = [
  "On-demand private jet charter, worldwide",
  "Helicopter transfers and resort connections",
  "Multi-stop and roadshow itineraries",
  "Group and delegation charter",
  "Catering, ground transport and concierge coordination",
  "Empty-leg opportunities as they genuinely arise",
];

export default function CharterPage() {
  return (
    <>
      <Seo
        title="Private Jet Charter | JS Private Aviation"
        description="On-demand private jet charter sourced through independent aircraft operators — one enquiry, considered options, everything arranged."
      />
      <KineticHero
        testId="charter-hero"
        fullHeight={false}
        overline="Private Jet Charter"
        lines={["Charter, arranged", "properly."]}
        sub="On-demand private jet charter sourced through independent aircraft operators — one enquiry, considered options, everything arranged."
        image={img(IMAGES.jetNight, 2000)}
      >
        <a
          href="#request"
          data-testid="charter-hero-cta"
          className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
        >
          Request a Quote
        </a>
        <a
          href={waLink("Hello — I would like to discuss a private charter.")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="charter-hero-whatsapp"
          className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <MessageCircle size={15} /> WhatsApp a Specialist
        </a>
      </KineticHero>

      <section className="bg-[#F9F9F6] py-28 sm:py-36">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <Overline>The process</Overline>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
              From enquiry to wheels-up.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.06 * i} className="bg-[#F9F9F6]">
                <div data-testid={`process-step-${s.n}`} className="h-full p-8 sm:p-10">
                  <span className="font-mono text-sm text-black/30">{s.n}</span>
                  <h3 className="mt-4 font-display text-xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-black/55">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-14">
            <Reveal>
              <Overline>What we arrange</Overline>
              <ul className="mt-8 space-y-4">
                {SCOPE.map((item) => (
                  <li key={item} className="flex items-baseline gap-4 border-b border-black/10 pb-4 text-[15px] text-black/75">
                    <span className="font-mono text-[10px] text-black/30">—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="flex h-full flex-col justify-between border border-black/10 bg-white p-10 sm:p-12">
                <p className="font-display text-2xl sm:text-[1.7rem] leading-snug tracking-tight text-black/85">
                  "We never advertise aircraft we don't have, prices we can't honour, or availability we
                  can't confirm. Every quote is sourced for your trip, at the time you ask."
                </p>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.28em] text-black/40">
                  The JS Private Aviation standard
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="request" data-testid="charter-enquiry-section" className="bg-[#F9F9F6] pb-28 sm:pb-36">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
          <Reveal>
            <EnquiryForm source="charter-page" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
